<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="flex flex-col w-full justify-center">
            <page-header> Phone Numbers </page-header>

            <team-phone-numbers-controls
                v-model="fetchParams"
                :contactGroups="contactGroups"
                @apply="applyFilters"
                @reset="resetFilters"
            ></team-phone-numbers-controls>

            <template v-if="!loading && !list.length">
                <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
                <empty-state v-else>No Phone Numbers yet.</empty-state>
            </template>

            <vx-table
                v-else-if="list.length"
                :class="{ 'xl:grid-cols-7-auto': !contactGroups, 'xl:grid-cols-8-auto': contactGroups }"
                rowBreakpoint="xl"
            >
                <vx-table-row isHeader>
                    <div>Number</div>
                    <div>Number Type</div>
                    <div>Country</div>
                    <div>Voice</div>
                    <div>Sending</div>
                    <div v-if="contactGroups">Group</div>
                    <div>Vendor</div>
                </vx-table-row>

                <kiosk-team-phone-number-row
                    v-for="item in list"
                    :key="item.id"
                    :modelValue="item"
                    :contactGroups="contactGroups"
                ></kiosk-team-phone-number-row>
            </vx-table>

            <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
                <vx-button
                    v-if="fetchParams.cursor"
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    data-test="load-more-btn"
                    class="grow-0"
                    :loading="loading"
                >
                    Load More
                </vx-button>
            </div>
        </div>
    </kiosk-team-details>
</template>

<script setup lang="ts">
import { VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { computed, onMounted } from 'vue';
import EmptyState from '~/components/general/EmptyState.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import TeamPhoneNumbersControls from '~/components/settings/teams/phone-numbers/TeamPhoneNumbersControls.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { phoneNumbersClient } from '~/services';
import type { PhoneNumber, PhoneNumberListParams } from '~/services/phoneNumbersClient.types';
import { isEqual } from '~/utils/helpers';
import { useTeamDetails } from '../composables/useTeamDetails';
import KioskTeamDetails from '../details/KioskTeamDetails.vue';
import KioskTeamPhoneNumberRow from './KioskTeamPhoneNumberRow.vue';

const props = defineProps<{
    teamId: number;
}>();

const { team } = useTeamDetails();
const contactGroups = computed(() => Boolean(team.value?.team_attributes?.contact_groups));

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<PhoneNumber, PhoneNumberListParams>();

const defaultFilters = (): PhoneNumberListParams => ({
    per_page: 10,
    cursor: null,
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    fetchRecords(phoneNumbersClient.listPhoneNumbers(props.teamId, { ...fetchParams.value }));
};
</script>
